import { useGet } from 'hooks/use-get';
import React, { useContext, useState } from 'react';

import { AxiosError } from 'axios';
import { UseQueryResult } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { getFeedRunOutDate, getOfdSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/get-ofd-settings';
import { useUpdateOFDSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/hooks/use-update-ofd-settings';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import {
  UpdateFeedReserveInput,
  UpdateConsumptionRateInput,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { FeedRunOut, OfdSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings.type';

export enum MutationType {
  FeedReserve = 'feedReserve',
  DailyConsumption = 'dailyConsumption',
  None = '',
}

type StateHandlers = {
  setFeedReserveInput: (data: UpdateFeedReserveInput) => void;
  setDailyConsumptionInput: (data: UpdateConsumptionRateInput) => void;
};

type State = {
  dailyConsumptionInput: UpdateConsumptionRateInput | undefined;
  feedReserveInput: UpdateFeedReserveInput | undefined;
  stateHandlers: StateHandlers;
  getOfdSettingsQuery: UseQueryResult<OfdSettings, AxiosError<unknown, any>>;
  getFeedRunOutDateQuery: UseQueryResult<FeedRunOut, AxiosError<unknown, any>>;
  handleMutation: (mutationType: MutationType) => void;
};

const InputSettingsContext = React.createContext<State | undefined>(undefined);

type InputSettingsProviderProps = {
  children: React.ReactNode;
};

const InputSettingsProvider: React.FC<InputSettingsProviderProps> = ({ children }) => {
  const params = useParams<OFD2Params>();
  const [feedReserveInput, setFeedReserveInput] = useState<UpdateFeedReserveInput>();
  const [dailyConsumptionInput, setDailyConsumptionInput] = useState<UpdateConsumptionRateInput>();

  const { updateDailyConsumptionRate, updateFeedReserve } = useUpdateOFDSettings();

  // #region FETCH OFD SETTINGS
  const getOfdSettingsQuery = useGet(['getOfdSettings', params.globalSiteId], ({ queryKey }) => getOfdSettings(String(queryKey[1])), {
    enabled: !!params.globalSiteId,
  });

  const getFeedRunOutDateQuery = useGet(
    ['fetchFeedRunOutDate', params.globalSiteId],
    ({ queryKey }) => getFeedRunOutDate(String(queryKey[1])),
    {
      enabled: false, // disabled for now, will be used to get the next delivery date.
    }
  );
  // #endregion FETCH OFD SETTINGS

  // #region HANDLERS
  const handleMutation = (mutationType: MutationType) => {
    switch (mutationType) {
      case MutationType.DailyConsumption:
        updateDailyConsumptionRate.mutate(dailyConsumptionInput as UpdateConsumptionRateInput);
        break;
      case MutationType.FeedReserve:
        updateFeedReserve.mutate(feedReserveInput as UpdateFeedReserveInput);
        break;
      default:
        break;
    }
  };

  // #endregion

  return (
    <InputSettingsContext.Provider
      value={{
        handleMutation,
        getOfdSettingsQuery,
        getFeedRunOutDateQuery,
        dailyConsumptionInput,
        feedReserveInput,
        stateHandlers: {
          setDailyConsumptionInput,
          setFeedReserveInput,
        },
      }}>
      {children}
    </InputSettingsContext.Provider>
  );
};

function useInputSettingsState() {
  const context = useContext(InputSettingsContext);
  if (context === undefined) {
    throw new Error('useInputSettingsState must be used within a InputSettings provider');
  }
  return context;
}

export { InputSettingsProvider, useInputSettingsState };
